import rosettaSdk from "@product/rosetta-sdk";
import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai/index";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useLatest } from "react-use";

import { config } from "shared/data";
import { useCurrentPageType, useFallbackStorageValue, useParsedCookie } from "shared/hooks";
import { getAccountState } from "shared/lib/account";
import { PageType } from "shared/lib/page-type";
import { isExternalScriptsDisabled, notEmpty, parseQueryString } from "shared/lib/utils";

import { useRosettaCampaignPopup } from "scmp-app/components/popup-container/rosetta-campaign-popup/hooks";
import { getSectionWidgets } from "scmp-app/components/section/default-section/helpers";
import { sendGA4UserPropertiesTracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useRosettaContext, useRosettaEventHandlers } from "scmp-app/lib/rosetta";
import { useSetStandardPaywallABTests } from "scmp-app/lib/rosetta/a-b-test/hooks";
import { setPianoCustomVariable } from "scmp-app/lib/rosetta/helpers";
import { ScmpSubscriptionsKey } from "scmp-app/lib/rosetta/hooks/consts";
import { useRosettaPianoExecution } from "scmp-app/lib/rosetta/hooks/rosetta-piano-execution";

import { useRosettaConfig } from "./rosetta-config";
import { useRosettaSetState } from "./rosetta-set-state";

function parseEnvironment(s: string) {
  switch (s.toLocaleLowerCase()) {
    case "production":
      return "production";
    default:
      return "development";
  }
}

function parsePianoEnvironment(s: string) {
  switch (s.toLocaleLowerCase()) {
    case "production":
      return "production";
    default:
      return "sandbox";
  }
}

export const useRosettaSetup = () => {
  const initialized = useRef(false);
  const { abandonedCartFlow } = useRosettaContext();
  const latestReferences = useLatest({ abandonedCartFlow });

  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { isRosettaPianoExecutionReady, shouldSkipPianoExecution } = useRosettaPianoExecution(
    navigatorInfo,
    navigatorInfoLoaded,
  );
  const { registerRosettaCampaignPopupService } = useRosettaCampaignPopup();
  const { handleUpdateRosettaState } = useRosettaSetState();
  const { user } = getAccountState();
  const { update: updateScmpSubscriberCookie } = useParsedCookie("scmp_subscriber", raw => raw, {
    domain: config.account.cookieDomain,
    expires: 1,
  });
  // TODO: temporary add this for print article
  const { set: setScmpSubscriberStorage } = useFallbackStorageValue<string>("scmp_subscriber", {
    parse: value => value,
    stringify: value => value,
  });

  const { set: setScmpSubscriptionsStorage } = useFallbackStorageValue<string[]>(
    ScmpSubscriptionsKey,
    {
      defaultValue: [],
    },
  );

  const currentPageType = useCurrentPageType();
  const { attachEventHandlers } = useRosettaEventHandlers();

  const { query } = useRouter();
  const entityId = parseQueryString(query.entityId) ?? "";

  const [_, { execute }] = useAsync(async () => {
    if (initialized.current || isExternalScriptsDisabled("piano")) return;
    initialized.current = true;

    rosettaSdk
      .setConfig({
        contentService: {
          apiKey: config.rosetta.contentService.apiKey,
          apiUri: `${config.rosetta.apiGatewayHost}/content-delivery/v2`,
          env: parseEnvironment(config.rosetta.contentService.environment),
        },
        piano: {
          aid: config.rosetta.piano.applicationId,
          apiUri: config.rosetta.piano.apiUri,
          env: parsePianoEnvironment(config.rosetta.piano.environment),
        },
        rosetta: {
          env: parseEnvironment(config.rosetta.environment),
          uri: `${config.rosetta.subscribeHost}`,
        },
      })
      .setPageUrl(`${location.origin}${location.pathname}`);

    rosettaSdk.registerService("geo", {
      apiKey: config.rosetta.contentService.apiKey,
      apiUri: `${config.rosetta.apiGatewayHost}/gloc/v1`,
    });

    rosettaSdk.registerService("tierInfo", {
      apiKey: config.rosetta.contentService.apiKey,
      apiUri: `${config.corporateSubscriber.host}/tier`,
    });

    rosettaSdk.registerService("pianoUserInfo", {
      aid: config.rosetta.piano.applicationId,
      apiUri: `${config.rosetta.subscribeHost}/api/user`,
    });

    rosettaSdk.registerService("pianoPublisherConversions", {
      aid: config.rosetta.piano.applicationId,
      apiUri: `${config.rosetta.subscribeHost}/api/user/conversions`,
    });

    rosettaSdk.registerService("onboarding");

    rosettaSdk.registerService("mastheadRenewPopUp");

    registerRosettaCampaignPopupService();

    const handleGTMSubscribeVariables = (user?: Rosetta.SDK.RulesEngineTypes.Outcomes["user"]) => {
      const { tier } = user ?? {};
      if (notEmpty(tier)) sendGA4UserPropertiesTracking({ tier });
    };

    const handlePianoCustomVariables = (
      tracking?: Rosetta.SDK.RulesEngineTypes.Outcomes["tracking"],
    ) => {
      setPianoCustomVariable("SUBSCRIBER_STATUS", tracking?.subscriberStatus);

      const username =
        user?.username?.split?.("@")?.[0] ?? `${user?.firstname ?? ""} ${user?.lastname ?? ""}`;
      if (notEmpty(username?.trim())) setPianoCustomVariable("USER_NAME", username);

      const getSubscribeUrl = () => {
        const subscribeUrl = new URL(config.rosetta.subscribeHost);
        subscribeUrl.searchParams.set("destination", location.origin);
        return subscribeUrl.toString();
      };
      setPianoCustomVariable("subscribeUrl", getSubscribeUrl());
    };

    try {
      await rosettaSdk.init();
      attachEventHandlers();
      await rosettaSdk.initRules();
      const { supportPiano: isSubscriptionEnabled = true } =
        rosettaSdk.rulesEngine.getOutcome("guest") ?? {};

      // Article page will be executed after variable has been set with apps/app/components/article/article-render/hooks/piano-integration/index.ts
      if (
        (currentPageType === PageType.Homepage ||
          currentPageType === PageType.Plus ||
          (currentPageType === PageType.Section && getSectionWidgets(entityId).includes("plus"))) &&
        shouldSkipPianoExecution === false
      ) {
        rosettaSdk.execute();
      }

      const rosettaUser = rosettaSdk.rulesEngine.getOutcome("user");
      const tracking = rosettaSdk.rulesEngine.getOutcome("tracking");
      const isScmpSubscriber = rosettaSdk.rulesEngine.getOutcome("user.isSCMPSubscriber");
      const isScmpPlusSubscriber = rosettaSdk?.rulesEngine.getOutcome("user.isSCMPPlusSubscriber");
      handleGTMSubscribeVariables(rosettaUser);
      handlePianoCustomVariables(tracking);
      updateScmpSubscriberCookie(tracking?.subscriberStatus ?? "");
      setScmpSubscriberStorage(tracking?.subscriberStatus ?? "");
      setScmpSubscriptionsStorage([...(isScmpPlusSubscriber ? ["scmp-plus"] : [])]);

      latestReferences?.current?.abandonedCartFlow?.eventBus.emit({
        data: {
          hasAccess: rosettaSdk.rulesEngine.getFact("hasActiveScmpConversions") ?? false,
        },
        name: "AccessStatusUpdated",
      });

      handleUpdateRosettaState(
        {
          contentServiceUserInfo: {
            isSubscriptionEnabled,
          },
          instance: rosettaSdk,
          isScmpPlusSubscriber,
          isScmpSubscriber,
          tracking,
          user: rosettaUser,
        },
        "success",
      );

      rosettaSdk.getService("mastheadRenewPopUp")?.addMastheadRenewPopUpScript();
    } catch (error) {
      console.error("rosettaSdk init error", error);
      handleUpdateRosettaState(
        undefined,
        "error",
        error instanceof Error ? error : new Error(JSON.stringify(error)),
      );
    }
    try {
      void rosettaSdk.initiateSwGLinkingFlow();
    } catch (error) {
      console.error("rosettaSdk initiateSwGLinkingFlow", error);
    }
  });

  useEffect(() => {
    if (initialized.current || !isRosettaPianoExecutionReady) return;
    void execute();
  }, [execute, isRosettaPianoExecutionReady]);

  const { rosettaConfigState } = useRosettaConfig();
  useEffect(() => {
    const {
      isShowPremiumPromo = false,
      litePaywallTimeFrame = 3,
      oldArticleTimeframe = 15,
      paywallCampaign,
    } = rosettaConfigState.result?.paywallSettings ?? {};
    handleUpdateRosettaState({
      appSettings: {
        isShowPremiumPromo,
        litePaywallTimeFrame,
        oldArticleTimeframe,
        paywallCampaignName: paywallCampaign?.name,
      },
    });
  }, [handleUpdateRosettaState, rosettaConfigState]);

  useSetStandardPaywallABTests();

  useEffect(() => {
    if (currentPageType !== PageType.Article) return;

    let paywallCheckoutSetupCleanup:
      | ReturnType<typeof rosettaSdk.setupPaywallCheckoutForPWA>["cleanup"]
      | undefined;
    let isCancelled = false;
    const handleOnReady = () => {
      if (isCancelled) return;

      paywallCheckoutSetupCleanup = rosettaSdk.setupPaywallCheckoutForPWA({
        account: {
          debugLog: config.account.debugLog,
          gtmContainerId: config.googleTagManager.containerId,
          isInWebView: false,
          recaptchaContainerId: config.account.recaptchaContainerId,
          recaptchaSiteKey: config.account.recaptchaSiteKey,
          url: config.account.scmpAccountHost,
        },
        scmp: {
          url: `https://${config.domain.scmp}`,
        },
      })?.cleanup;
    };

    rosettaSdk.on("onReady", handleOnReady);

    return () => {
      isCancelled = true;
      paywallCheckoutSetupCleanup?.();
      rosettaSdk.off("onReady", handleOnReady);
    };
  }, [currentPageType]);
};
